import type { PlayerRatingAllResponse } from '@on3/api';
import {
  ServiceLogo,
  type ServiceType,
} from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import { webUrl } from '@on3/ui-lib/utils/api';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { type ReactNode, useCallback } from 'react';
import { StarRatings } from 'src/components/UI/StarRatings';

import styles from './RankingServiceModule.module.scss';

interface ConditionalWrapperProps {
  condition: boolean;
  wrapper: any;
  link: any;
  children?: ReactNode;
}

// This conditionally Wraps the Component with a link or div.
const ConditionalWrapper = ({
  condition,
  wrapper,
  link,
  children,
}: ConditionalWrapperProps) =>
  condition ? link(children || '') : wrapper(children || '');

export const RankingServiceModule = ({
  change,
  link,
  overallRank,
  positionAbbr,
  positionRank,
  rating,
  stars,
  stateAbbr,
  stateRank,
  rankingYear,
  type,
}: PlayerRatingAllResponse) => {
  const router = useRouter();
  const profileLink = `${webUrl}/rivals/${router.query.id}/recruiting/`;
  const isRivalsRating = !!rankingYear && rankingYear > 2025;

  const logoType = isRivalsRating && type === 'On3' ? 'Rivals' : type;

  if (type?.toLowerCase() === 'on3') {
    link = profileLink;
  }

  const divComponent = useCallback(
    (children: React.ReactNode) => {
      return (
        <div className={clsx(styles.serviceWrapper, !rating && styles.blank)}>
          {children}
        </div>
      );
    },
    [rating],
  );

  const linkComponent = useCallback(
    (children: React.ReactNode) => {
      return (
        <a
          className={styles.serviceWrapper}
          href={link || ''}
          rel="noopener noreferrer"
          target="_blank"
        >
          {children}
        </a>
      );
    },
    [link],
  );

  return (
    <ConditionalWrapper
      condition={link ? true : false}
      link={linkComponent}
      wrapper={divComponent}
    >
      <ServiceLogo type={logoType as ServiceType} />
      <div className={styles.ratingContainer}>
        <span className={styles.ratingValue}>
          <StarRatings stars={stars} />
          {rating
            ? type?.toLowerCase() === 'rivals'
              ? rating?.toFixed(1)
              : rating
            : '-'}
        </span>
      </div>
      <div className={styles.rankingContainer}>
        <span className={styles.rankingLabel}>
          <span className={styles.label}>NATL</span>
          <span className={styles.rankingNumber}>{overallRank || '-'}</span>
        </span>
        <span className={styles.rankingLabel}>
          <span className={styles.label}>{positionAbbr || 'POS'}</span>
          <span className={styles.rankingNumber}>{positionRank || '-'}</span>
        </span>
        <span className={styles.rankingLabel}>
          <span className={styles.label}>{stateAbbr || 'ST'}</span>
          <span className={styles.rankingNumber}>{stateRank || '-'}</span>
        </span>
      </div>
    </ConditionalWrapper>
  );
};
